import { useEffect, useMemo, useRef, useState } from "react";

import { useQuery } from "@apollo/client/react";

import { ConversationDraftSectionsDocument } from "@app_schema";

import { useActionCableSubscription } from "@application/hooks/use_action_cable_subscription";

import { coalesce, reconnected } from "../../conversations/live_updates";
import { type AssistantChats } from "./use_assistant_chats";
import { type DraftingSections } from "./use_drafting_sections";

// Kept the same between renders: the Report refills before paint on a new one.
const NONE: [] = [];

// The case's newest draft and the chat's sections, when the chat wrote for that draft; else the sections
// he edited, which every chat shows. They refill while the assistant answers, and once more when it's done.
export const useDraftSections = ({
  drafting,
  chats,
  enabled,
  filesKey,
}: {
  drafting: DraftingSections;
  chats: AssistantChats;
  enabled: boolean;
  // Changes as the case's drafts are uploaded and read, so the newest is shown.
  filesKey: string;
}) => {
  const { draft, refetch: refetchCase } = drafting;
  useEffect(() => {
    if (enabled) void refetchCase();
  }, [enabled, filesKey, refetchCase]);
  const id = chats.current?.id;
  const skip = !enabled || !id;
  const { data, refetch } = useQuery(ConversationDraftSectionsDocument, {
    variables: { id: id ?? "" },
    skip,
    fetchPolicy: "cache-and-network",
  });
  useEffect(() => {
    if (!skip && !chats.running) void refetch();
  }, [skip, chats.running, refetch]);
  useLiveSections({
    id: skip ? undefined : id,
    running: chats.running,
    refetch,
  });

  // An older chat wrote for an older draft: its answers stay in the chat.
  const conversation =
    skip || !draft || !data?.conversation.onCaseDraft
      ? undefined
      : data.conversation;
  const edited = drafting.editedSections;
  return {
    draft,
    // Before a chat, only his edits fill the Report and its download.
    edited: !conversation && edited.length > 0,
    // His template, which Edit your template opens.
    hasTemplate: drafting.hasTemplate,
    // A section Draft all can ask for.
    draftable: drafting.draftable,
    conversationId: conversation?.id,
    sections: conversation?.draftSections ?? edited,
    notApplied: conversation?.draftNotAppliedNotice ?? null,
    writingNotes: conversation?.draftWritingNotes ?? false,
    // What its drafted sections cite from sources they don't read.
    citationCount: conversation?.draftCitationCount ?? 0,
    writingSection: conversation?.draftWritingSection ?? null,
    writingMarker: conversation?.draftWritingMarker ?? null,
    writingLabel: conversation?.draftWritingLabel ?? null,
    statuses: new Map(
      (conversation?.draftingSectionStatuses ?? NONE).map(
        ({ sectionId, status, statusLine, alert, citations }) => [
          sectionId,
          { status, statusLine, alert, citations },
        ],
      ),
    ),
  };
};

// Refetches the sections on each update the chat's channel sends while the
// assistant answers (the same updates the chat follows), one request at a time,
// and once on a reconnect, since updates may have been missed.
const useLiveSections = ({
  id,
  running,
  refetch,
}: {
  id: string | undefined;
  running: boolean;
  refetch: () => Promise<unknown>;
}) => {
  const latest = useRef({ running, refetch });
  latest.current = { running, refetch };
  const [update] = useState(() => coalesce(() => latest.current.refetch()));

  const channel = useMemo(
    () => (id ? { channel: "ConversationChannel", id } : null),
    [id],
  );
  const status = useActionCableSubscription(channel, () => {
    if (latest.current.running) update();
  });

  const [isReconnect] = useState(() => reconnected());
  useEffect(() => {
    if (isReconnect(status)) update();
  }, [status, isReconnect, update]);
};
